/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { sanitizeHtml } from './sanitizeHtml';

/**
 * Checks whether a string contains HTML tags.
 */
function containsHTML(text: string): boolean {
  return /<[a-z][\s\S]*>/i.test(text);
}

/**
 * Renders a content string that may be plain text (with markdown-style links)
 * or HTML (from the WYSIWYG editor). Returns JSX in either case.
 */
export const renderTextWithLinks = (text: string) => {
  if (!text) return text;

  // If the content is HTML (from the WYSIWYG editor), render it directly.
  // Pre-process any markdown-style [label](url) links that may have been typed
  // instead of using the link tool — convert them to proper <a> tags first.
  if (containsHTML(text)) {
    const html = sanitizeHtml(
      text.replace(
        /\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g,
        '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>'
      )
    );
    return <span dangerouslySetInnerHTML={{ __html: html }} className="rich-content" />;
  }

  // Legacy: parse markdown-style [label](url) links in plain text
  const parts = text.split(/(\[[^\]]+\]\([^)]+\))/g);

  return parts.map((part, index) => {
    const match = part.match(/\[([^\]]+)\]\(([^)]+)\)/);
    if (match) {
      return (
        <a
          key={index}
          href={match[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="text-emerald-400 hover:text-emerald-300 font-bold underline underline-offset-4 decoration-emerald-500/30 transition-colors"
        >
          {match[1]}
        </a>
      );
    }
    return part;
  });
};
